import {
  getAccessTokenWithFallback,
  initShellAuth,
  isInShell,
  clearTokenCache,
} from '@harleys/shell-client';
import { PublicClientApplication, Configuration, InteractionRequiredAuthError } from '@azure/msal-browser';
import { AuthenticatedUser } from '../types';
import { isDesignPreview, previewUser } from './preview';

const CLIENT_ID = import.meta.env.VITE_AAD_CLIENT_ID as string;
const TENANT_ID = import.meta.env.VITE_AAD_TENANT_ID as string;
const API_SCOPE = import.meta.env.VITE_API_SCOPE as string;

const msalConfig: Configuration = {
  auth: {
    clientId: CLIENT_ID,
    authority: `https://login.microsoftonline.com/${TENANT_ID}`,
    redirectUri: window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

const msalInstance = new PublicClientApplication(msalConfig);
let msalInitialised = false;

initShellAuth();

async function ensureMsalReady(): Promise<void> {
  if (!msalInitialised) {
    await msalInstance.initialize();
    await msalInstance.handleRedirectPromise();
    msalInitialised = true;
  }
}

function parseJwt<T = Record<string, unknown>>(token: string): T {
  const base64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
  const json = decodeURIComponent(
    atob(base64)
      .split('')
      .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
      .join('')
  );
  return JSON.parse(json) as T;
}

interface EntraTokenClaims {
  oid: string;
  name: string;
  preferred_username?: string;
  email?: string;
  upn?: string;
}

async function getMsalToken(): Promise<string> {
  await ensureMsalReady();
  const accounts = msalInstance.getAllAccounts();
  const account = accounts[0];

  if (!account) {
    await msalInstance.loginPopup({ scopes: [API_SCOPE] });
    return getMsalToken();
  }

  try {
    const result = await msalInstance.acquireTokenSilent({
      scopes: [API_SCOPE],
      account,
    });
    return result.accessToken;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      const result = await msalInstance.acquireTokenPopup({ scopes: [API_SCOPE] });
      return result.accessToken;
    }
    throw err;
  }
}

async function msalFallback(): Promise<{ token: string; expiresAt: number }> {
  const token = await getMsalToken();
  const claims = parseJwt<{ exp: number }>(token);
  return { token, expiresAt: claims.exp * 1000 };
}

export async function getAccessToken(): Promise<string> {
  return getAccessTokenWithFallback(msalFallback);
}

export async function getCurrentUser(): Promise<AuthenticatedUser> {
  if (isDesignPreview) return previewUser;
  const token = await getAccessToken();
  const claims = parseJwt<EntraTokenClaims>(token);
  return {
    name: claims.name,
    email: claims.email || claims.preferred_username || claims.upn || '',
    oid: claims.oid,
  };
}

export { clearTokenCache, isInShell };
